import { itemsApi } from "#/api/itemsApi";
import { productApi } from "#/api/productApi";
import { ticketsApi } from "#/api/ticketsApi";
import ItemStatusSelect from "#/components/inputs/ItemStatusSelect";
import { showSuccessNotification } from "#/components/notifications";
import Popover from "#/components/overlays/Popover";
import SuggestedItemsPicker from "#/components/tickets/SuggestedItemsPicker";
import type { Item } from "#/types/items";
import type { CreateTicketPayload } from "#/types/tickets";
import {
  Button,
  Drawer,
  MultiSelect,
  Radio,
  RadioGroup,
  Select,
  Textarea,
  TextInput,
  type ComboboxItem,
  type DrawerProps,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { IconChevronDown, IconSend2, IconUpload } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { logger } from "#/utils/logger";

export interface Props extends DrawerProps {
  itemIds?: number[];
  onSuccess: () => void;
}

type LinkType = "" | "item" | "category" | "none";

const ITEM_SEARCH_MIN_CHARS = 2;
const ITEM_SEARCH_DEBOUNCE_MS = 400;
const ITEM_SEARCH_LIMIT = 20;

const fallbackItemLabel = (id: number | string): string => `Item #${id}`;

/** "EXT-0042 · 300833B2DDD9 · Cages" — external id (or "Item #id"), first RFID, category. */
const buildItemLabel = (item: Item): string => {
  const externalId =
    typeof item.external_id === "string" ? item.external_id.trim() : "";
  const parts: string[] = [externalId || fallbackItemLabel(item.id)];

  const firstRfid = Array.isArray(item.rfid_tags)
    ? item.rfid_tags.find(
        (tag) => typeof tag?.rfid === "string" && tag.rfid.trim() !== ""
      )?.rfid
    : undefined;
  if (firstRfid) parts.push(firstRfid.trim());

  const categoryName =
    typeof item.product?.name === "string" ? item.product.name.trim() : "";
  if (categoryName) parts.push(categoryName);

  return parts.join(" · ");
};

const CreateTicketDrawer = ({ itemIds, onSuccess, ...props }: Props) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [showSendOptions, setShowSendOptions] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [suggestedItemIds, setSuggestedItemIds] = useState<number[]>([]);
  const [itemSearch, setItemSearch] = useState<string>("");
  const [debouncedItemSearch] = useDebouncedValue(
    itemSearch,
    ITEM_SEARCH_DEBOUNCE_MS
  );
  // Selected item id -> label, so chips keep their label once the search results change.
  const [selectedItemLabels, setSelectedItemLabels] = useState<
    Record<string, string>
  >({});

  const hasPreselectedItems = itemIds && itemIds.length > 0;

  const form = useForm({
    initialValues: {
      link_type: "" as LinkType,
      item_ids: [] as string[],
      product_id: null as string | null,
      item_status: "",
      deadline: "",
      notify_email: "",
      person_in_charge: "",
      submitType: "send",
      status: "todo",
      problem_details: "",
    },
    validate: {
      link_type: (value) => {
        if (hasPreselectedItems) return null;
        return value ? null : t("tickets.validation.linkTypeRequired");
      },
      item_ids: (value, values) => {
        if (hasPreselectedItems || values.link_type !== "item") return null;
        return Array.isArray(value) && value.length > 0
          ? null
          : t("tickets.validation.itemsRequired");
      },
      product_id: (value, values) => {
        if (hasPreselectedItems || values.link_type !== "category") return null;
        return value ? null : t("tickets.validation.categoryRequired");
      },
      item_status: (value) => (value ? null : t("tickets.validation.statusRequired")),
      deadline: (value) => (value ? null : t("tickets.validation.deadlineRequired")),
      person_in_charge: (value) =>
        value ? null : t("tickets.validation.personInChargeRequired"),
      notify_email: (value) => {
        if (!value) return t("tickets.validation.notifyEmailRequired");
        if (!/^\S+@\S+\.\S+$/.test(value)) return t("tickets.validation.validEmail");
        return null;
      },
      problem_details: (value, values) => {
        if (values.status !== "problem") return null;
        const trimmed = typeof value === "string" ? value.trim() : "";
        return trimmed ? null : t("tickets.validation.problemDetailsRequired");
      },
    },
    transformValues: (values) => ({
      ...values,
      deadline: values.deadline
        ? moment(values.deadline).format("YYYY-MM-DD")
        : "",
    }),
  });

  const linkType = form.values.link_type;
  const trimmedItemSearch = debouncedItemSearch.trim();

  // Server-side item search for the multi-select (debounced, min chars, limited).
  const { data: searchedItems, isFetching: itemsFetching } = useQuery({
    queryKey: ["items-for-ticket", trimmedItemSearch],
    queryFn: async () => {
      const response = await itemsApi.getAll({
        search: trimmedItemSearch,
        limit: ITEM_SEARCH_LIMIT,
      });
      return response.data.data ?? [];
    },
    enabled:
      !hasPreselectedItems &&
      linkType === "item" &&
      trimmedItemSearch.length >= ITEM_SEARCH_MIN_CHARS,
  });

  // Fetch products for the category select
  const { data: productsData } = useQuery({
    queryKey: ["products-for-ticket"],
    queryFn: async () => {
      const response = await productApi.getAll({ limit: 100000 });
      return response.data.data;
    },
    enabled: !hasPreselectedItems && linkType === "category",
  });

  // Options = search results UNION already-selected items (so chips never lose their label).
  const itemOptions = useMemo<ComboboxItem[]>(() => {
    const options: ComboboxItem[] = (searchedItems ?? []).map((item) => ({
      value: String(item.id),
      label: buildItemLabel(item),
    }));
    const present = new Set(options.map((option) => option.value));
    const selectedOnly: ComboboxItem[] = form.values.item_ids
      .filter((id) => !present.has(id))
      .map((id) => ({
        value: id,
        label: selectedItemLabels[id] ?? fallbackItemLabel(id),
      }));
    return [...selectedOnly, ...options];
  }, [searchedItems, form.values.item_ids, selectedItemLabels]);

  // Still "searching" while the debounce has not caught up with what was typed.
  const itemsSearching =
    itemsFetching || itemSearch.trim() !== trimmedItemSearch;

  const itemNothingFoundMessage =
    itemSearch.trim().length < ITEM_SEARCH_MIN_CHARS
      ? t("tickets.form.itemSearchMin")
      : itemsSearching
        ? t("tickets.form.itemSearching")
        : t("tickets.form.itemNoResults");

  const handleItemsChange = (values: string[]) => {
    form.setFieldValue("item_ids", values);
    setSelectedItemLabels((previous) => {
      const next: Record<string, string> = {};
      for (const id of values) {
        next[id] =
          previous[id] ??
          itemOptions.find((option) => option.value === id)?.label ??
          fallbackItemLabel(id);
      }
      return next;
    });
  };

  const handleLinkTypeChange = (value: string | null) => {
    const nextLinkType: LinkType =
      value === "item" || value === "category" || value === "none" ? value : "";
    form.setFieldValue("link_type", nextLinkType);
    form.setFieldValue("item_ids", []);
    form.setFieldValue("product_id", null);
    setSuggestedItemIds([]);
    setSelectedItemLabels({});
    setItemSearch("");
  };

  const handleProductChange = (value: string | null) => {
    form.setFieldValue("product_id", value);
    setSuggestedItemIds([]);
  };

  const handleSubmit = async (values: typeof form.values) => {
    setSending(true);

    const { submitType, link_type, item_ids, product_id, ...withoutType } = values;

    // Determine which items/products to send
    let itemsToSend: number[] = [];
    let productsToSend: number[] = [];

    if (hasPreselectedItems) {
      // Use pre-selected items from props (existing flow)
      itemsToSend = itemIds!;
    } else {
      // Use selected items/products from form (new flow)
      if (link_type === "item") {
        itemsToSend = item_ids.map((id) => Number(id));
      } else if (link_type === "category") {
        productsToSend = [Number(product_id)];
        // Suggested items the user kept ticked are attached to the ticket.
        itemsToSend = suggestedItemIds;
      }
      // If link_type === "none", both arrays remain empty
    }

    const cleanedValues = {
      ...withoutType,
      items: itemsToSend.length > 0 ? itemsToSend : undefined,
      products: productsToSend.length > 0 ? productsToSend : undefined,
    };

    try {
      if (submitType === "sendAndExportPdf") {
        await ticketsApi.createAndExportPdf(
          cleanedValues as CreateTicketPayload
        );
      } else {
        await ticketsApi.create(cleanedValues as CreateTicketPayload);
      }
      onSuccess();
      setSending(false);
      showSuccessNotification({
        message:
          submitType === "sendAndExportPdf"
            ? t("tickets.createdAndExportedSuccess")
            : t("tickets.createdSuccess"),
      });
      resetForm();
    } catch (error) {
      logger.error("CreateTicketDrawer handleSubmit error:", error);
      setSending(false);
    }
  };

  const resetForm = () => {
    form.reset();
    setSuggestedItemIds([]);
    setSelectedItemLabels({});
    setItemSearch("");
  };

  return (
    <Drawer
      {...props}
      position={"right"}
      title={t("tickets.createTicket")}
      closeOnClickOutside={false}
      size={"lg"}
      onClose={() => {
        resetForm();
        props.onClose();
      }}
    >
      <form className="space-y-4" onSubmit={form.onSubmit(handleSubmit)}>
        {!hasPreselectedItems && (
          <>
            <Select
              label={t("tickets.form.whatIsLinkedTo")}
              required
              placeholder={t("tickets.form.selectLinkType")}
              data={[
                { label: t("tickets.form.item"), value: "item" },
                { label: t("tickets.form.category"), value: "category" },
                { label: t("tickets.form.none"), value: "none" },
              ]}
              {...form.getInputProps("link_type")}
              onChange={handleLinkTypeChange}
            />

            {linkType === "item" && (
              <MultiSelect
                label={t("tickets.form.selectItems")}
                required
                placeholder={t("tickets.form.itemSearchPlaceholder")}
                data={itemOptions}
                searchable
                clearSearchOnChange={false}
                searchValue={itemSearch}
                onSearchChange={setItemSearch}
                filter={({ options }) => options}
                nothingFoundMessage={itemNothingFoundMessage}
                {...form.getInputProps("item_ids")}
                onChange={handleItemsChange}
              />
            )}

            {linkType === "category" && (
              <Select
                label={t("tickets.form.selectCategory")}
                required
                placeholder={t("tickets.form.selectCategory")}
                data={
                  productsData?.map((product) => ({
                    label: product.name,
                    value: String(product.id),
                  })) || []
                }
                searchable
                clearable
                {...form.getInputProps("product_id")}
                onChange={handleProductChange}
              />
            )}
          </>
        )}

        <ItemStatusSelect
          label={t("tickets.form.itemStatus")}
          required
          placeholder={t("tickets.form.itemStatusPlaceholder")}
          {...form.getInputProps("item_status")}
        />

        {!hasPreselectedItems && linkType === "category" && (
          <SuggestedItemsPicker
            productId={form.values.product_id ? Number(form.values.product_id) : null}
            status={form.values.item_status}
            selectedIds={suggestedItemIds}
            onSelectionChange={setSuggestedItemIds}
          />
        )}

        <DateInput
          label={t("tickets.form.deadlineDate")}
          required
          placeholder={t("tickets.form.deadlinePlaceholder")}
          minDate={new Date()}
          {...form.getInputProps("deadline")}
        />

        <TextInput
          label={t("tickets.form.notifyInformation")}
          required
          placeholder={t("tickets.form.notifyEmailPlaceholder")}
          type="email"
          {...form.getInputProps("notify_email")}
        />

        <TextInput
          label={t("tickets.form.personInCharge")}
          required
          placeholder={t("tickets.form.personInChargePlaceholder")}
          {...form.getInputProps("person_in_charge")}
        />

        <RadioGroup
          label={t("tickets.form.markAs")}
          required
          variant="horizonal"
          {...form.getInputProps("status")}
        >
          <Radio label={t("tickets.form.orderToPerform")} value="todo" />
          <Radio label={t("tickets.form.orderInProgress")} value="in_progress" />
          <Radio label={t("tickets.form.problem")} value="problem" />
        </RadioGroup>

        {form.values.status === "problem" && (
          <Textarea
            label={t("tickets.form.problemDetails")}
            required
            classNames={{
              input: "h-24",
            }}
            placeholder={t("tickets.form.problemPlaceholder")}
            {...form.getInputProps("problem_details")}
          />
        )}

        <div className="w-full grid grid-cols-2 gap-4">
          <Button
            color="black"
            variant="outline"
            type="reset"
            onClick={resetForm}
          >
            {t("common.clear")}
          </Button>
          <Popover
            opened={showSendOptions}
            onChange={setShowSendOptions}
            classNames={{
              overlay: "backdrop-blue-none",
            }}
            width={250}
            anchorClassName="w-full"
            anchor={
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  color="black"
                  variant="filled"
                  fullWidth
                  rightSection={<IconChevronDown size={20} />}
                  onClick={() => setShowSendOptions(true)}
                  loading={sending}
                  disabled={!canWrite()}
                >
                  {t("common.send")}
                </Button>
              </PermissionTooltip>
            }
          >
            <div className="flex flex-col gap-2">
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  color="black"
                  variant="filled"
                  leftSection={<IconSend2 size={20} />}
                  fullWidth
                  type="button"
                  disabled={!canWrite()}
                  onClick={() => {
                    setShowSendOptions(false);
                    form.setFieldValue("submitType", "send");
                    form.onSubmit(handleSubmit)();
                  }}
                >
                  {t("common.send")}
                </Button>
              </PermissionTooltip>

              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  color="black"
                  variant="outline"
                  leftSection={<IconUpload size={20} />}
                  fullWidth
                  type="button"
                  disabled={!canWrite()}
                  onClick={() => {
                    setShowSendOptions(false);
                    form.setFieldValue("submitType", "sendAndExportPdf");
                    form.onSubmit(handleSubmit)();
                  }}
                >
                  {t("tickets.form.sendAndExportPdf")}
                </Button>
              </PermissionTooltip>
            </div>
          </Popover>
        </div>
      </form>
    </Drawer>
  );
};

export default CreateTicketDrawer;
